import React, { useEffect, useCallback } from 'react';
import { ComponentWrapper } from '@ihsm/ui-shared';
import { ESCAPE_KEY } from 'shared/constants';
import { modalCss } from '../utils';
import { ModalProps } from '../types';

export const ModalComponent: React.FC<Omit<ModalProps, 'usePortal'>> = ({
  isVisible,
  setVisibility,
  children,
  canEscape,
  className: customClassName,
  overrideCss = false,
  displayCloseButton,
  modalSize,
  header,
}): JSX.Element => {
  const {
    modalClassCss,
    modalBackgroundCss,
    modalCloseButtonCss,
    modalHeaderCss,
    modalContentCss,
  } = modalCss({
    customClassName,
    overrideCss,
    header: !!header,
  });

  const handleKeyEvent = useCallback(
    (event: React.KeyboardEvent) => {
      if (canEscape && event.key === ESCAPE_KEY) {
        setVisibility(!isVisible);
      }
    },
    [canEscape, isVisible, setVisibility]
  );

  const escapeHandler = useCallback(
    (event: KeyboardEvent) => {
      if (canEscape && event.key === ESCAPE_KEY) {
        setVisibility(!isVisible);
      }
    },
    [canEscape, isVisible, setVisibility]
  );

  const clickOutsideHandler = useCallback(() => {
    if (canEscape) {
      setVisibility(!isVisible);
    }
  }, [canEscape, isVisible, setVisibility]);

  const clickHandler = useCallback(() => {
    setVisibility(!isVisible);
  }, [isVisible, setVisibility]);

  useEffect(() => {
    document.addEventListener('keydown', escapeHandler);
    return () => {
      document.removeEventListener('keydown', escapeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        role="none"
        className={modalBackgroundCss}
        onClick={clickOutsideHandler}
        onKeyPress={handleKeyEvent}
      />
      <div
        className={modalClassCss}
        aria-modal
        aria-label="Modal Details"
        role="dialog"
        style={{ height: modalSize?.height, width: modalSize?.width }}
      >
        {header ? (
          <div className={modalHeaderCss}>
            <ComponentWrapper component={header} />
          </div>
        ) : null}

        <div className={modalContentCss}>{children}</div>

        {displayCloseButton ? (
          <span
            tabIndex={0}
            role="button"
            className={modalCloseButtonCss}
            aria-label="Close Modal Details"
            onClick={clickHandler}
            onKeyPress={handleKeyEvent}
          >
            &times;
          </span>
        ) : null}
      </div>
    </>
  );
};
